import { Flex } from 'components/Flex';
import { BKJButton } from '@awais4123/fillify-design-library';
import { FC, useState } from 'react';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { QuerySwitch } from 'components/QuerySwitch';
import {
  RemovePositionDetailsHeaderWrapper,
  H3,
  PositionName,
  Text,
  TextLabel,
  ShiftsTableSubheader,
  JanesTableWrapper,
  RemovePositionDetailsLocationWrapper,
  BoldText,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/RemovePositionModal.styled';
import { useTranslation } from 'hooks/useTranslation';

import {
  removePositionModalRoutingService,
  useRemovePositionModalUIContext,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';
import { BKJStickyTableHead, BKJTableBody } from 'components/BKJTable/BKJTable.styled';
import { RemovePositionDialog } from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionDialog';
import { toastService } from 'services';
import { usePositionsAllocationSettingsPageDataContext } from 'pages/LocationSettingsPage/PositionsAllocation/PositionsAllocationSettingsPage.data.context';
import { useFetchAssignedGroupedPositionsContext } from 'pages/LocationSettingsPage/PositionsAllocation/FetchAssignedGroupedPositions.data.context';
import { OpenShiftsListCards } from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/views/OpenShifts/ListCards';
import { DeleteAllOpenShiftsDialog } from 'pages/LocationSettingsPage/PositionsAllocation/DeleteAllOpenShiftsDialog/DeleteAllOpenShiftsDialog';
import {
  OpenShiftsDataProvider,
  useOpenShiftsDataContext,
  OpenShiftsDataContext,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/views/OpenShifts/OpenShifts.data.context';

const OpenShiftsComponent: FC = () => {
  const [isRemovePositionConfirmationOpen, setIsRemovePositionConfirmationOpen] = useState(false);
  const [isDeleteAllOpenShiftsConfirmationOpen, setIsDeleteAllOpenShiftsConfirmationOpen] =
    useState(false);

  const { data, isLoading } = useOpenShiftsDataContext();
  const { locationId, positionId } = useRemovePositionModalUIContext();
  const { refetch: fetchAssignedPositions } = usePositionsAllocationSettingsPageDataContext();
  const { refetch } = useFetchAssignedGroupedPositionsContext();

  const { t } = useTranslation();

  const location = data?.fetchPosition?.serviceableCommunities.find(
    (community) => community.displayId === locationId,
  );
  const isOpenShiftsEmptyState = data?.fetchPosition?.configuredShifts?.totalCount === 0;
  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      height="100%"
      flexGrow={1}
    >
      {isRemovePositionConfirmationOpen && (
        <RemovePositionDialog
          positionId={Number(positionId)}
          communityId={locationId}
          communityName={location?.name}
          isRemovePositionConfirmationOpen={isRemovePositionConfirmationOpen}
          setIsRemovePositionConfirmationOpen={setIsRemovePositionConfirmationOpen}
          onSuccess={() => {
            toastService.success(t('success:SUCCESSFULLY_REMOVED_POSITION'));
            refetch && refetch();
            fetchAssignedPositions && fetchAssignedPositions();
            removePositionModalRoutingService.close();
          }}
        />
      )}
      {isDeleteAllOpenShiftsConfirmationOpen && (
        <DeleteAllOpenShiftsDialog
          positionId={Number(positionId)}
          communityId={locationId}
          communityName={location?.name}
          isDeleteAllOpenShiftsConfirmationOpen={isDeleteAllOpenShiftsConfirmationOpen}
          setIsDeleteAllOpenShiftsConfirmationOpen={setIsDeleteAllOpenShiftsConfirmationOpen}
        />
      )}

      <RemovePositionDetailsHeaderWrapper noBorder={!isOpenShiftsEmptyState}>
        <H3>{t('location_settings:REVIEW_OPEN_SHIFTS_POSITION_TEXT')}</H3>
        <Flex justifyContent="space-between" alignItems="center">
          <PositionName>
            {t('common:POSITION')}: {data?.fetchPosition?.name}
          </PositionName>
        </Flex>
        <RemovePositionDetailsLocationWrapper>
          <TextLabel>{t('common:LOCATION')}</TextLabel>
          <Text>{location?.name}</Text>
          {!isOpenShiftsEmptyState && (
            <TextLabel>{t('location_settings:REMOVE_POSITION_MODAL_DELETE_ALL_SHIFTS')}</TextLabel>
          )}
        </RemovePositionDetailsLocationWrapper>
      </RemovePositionDetailsHeaderWrapper>
      {isOpenShiftsEmptyState ? (
        <Flex height="100%" padding="40px 32px">
          <Text>{t('location_settings:OPEN_SHIFTS_LIST_EMPTY_STATE_MESSAGE')}</Text>
        </Flex>
      ) : (
        <JanesTableWrapper>
          <BKJTableBody>
            <BKJStickyTableHead>
              <ShiftsTableSubheader>
                <Flex justifyContent="space-between" alignItems="center">
                  <BoldText>{t('location_settings:OPEN_SHIFTS')}</BoldText>
                  <BKJButton
                    variant="RedText"
                    type="button"
                    disabled={isOpenShiftsEmptyState || isLoading}
                    onClick={() => {
                      setIsDeleteAllOpenShiftsConfirmationOpen(true);
                    }}
                  >
                    {t('location_settings:DELETE_ALL')}
                  </BKJButton>
                </Flex>
              </ShiftsTableSubheader>
            </BKJStickyTableHead>
            <OpenShiftsListCards />
          </BKJTableBody>
        </JanesTableWrapper>
      )}
      <BKJSideModalFooter justifyContent="flex-end" gap="8px">
        <BKJButton
          variant="PurpleOutlined"
          onClick={() => removePositionModalRoutingService.close()}
          type="button"
          width="180px"
        >
          {t('auth:CANCEL')}
        </BKJButton>
        <BKJButton
          variant="PurpleSolid"
          width="180px"
          type="button"
          disabled={!isOpenShiftsEmptyState}
          onClick={() => {
            setIsRemovePositionConfirmationOpen(true);
          }}
        >
          {t('auth:NEXT')}
        </BKJButton>
      </BKJSideModalFooter>
    </Flex>
  );
};

export const OpenShiftsView: FC = () => {
  return (
    <OpenShiftsDataProvider>
      <QuerySwitch context={OpenShiftsDataContext} component={OpenShiftsComponent} />
    </OpenShiftsDataProvider>
  );
};
